import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  getProducts, getCustomers, getPriceSchemes,
  getInventory, getInventoryItem, getBranchPrice, addPendingSale,
  setOfflineOrg, setCashierPrefix,
  saveBusinessInfoCache, urlToBase64,
} from '../lib/offlineDB';
import { onSyncUpdate } from '../lib/syncManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

// ── Silent token refresh helpers ────────────────────────────────────────────
// Decode JWT exp claim without a library (JWT payload is base64url JSON).
function getTokenExp(token) {
  try {
    return JSON.parse(atob(token.split('.')[1])).exp || 0;
  } catch { return 0; }
}

// Module-level guards so the refresh fires at most once per 5 minutes and
// never runs in parallel.
let _refreshing = false;
let _lastRefreshAt = 0;
const REFRESH_COOLDOWN_MS = 5 * 60 * 1000;   // 5 min between checks
const REFRESH_THRESHOLD_S = 6 * 3600;         // refresh when < 6 h remain

async function maybeSilentRefresh() {
  const token = localStorage.getItem('agripos_token');
  if (!token) return;
  if (_refreshing) return;
  if (Date.now() - _lastRefreshAt < REFRESH_COOLDOWN_MS) return;

  const exp = getTokenExp(token);
  const remaining = exp - Date.now() / 1000;
  if (remaining > REFRESH_THRESHOLD_S) return;   // plenty of time left
  if (remaining <= 0) return;                     // already expired — let 401 handle it

  _refreshing = true;
  _lastRefreshAt = Date.now();
  try {
    const res = await api.post('/auth/refresh');
    const newToken = res.data.token;
    localStorage.setItem('agripos_token', newToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    // Notify the AuthProvider so its token state stays in sync
    window.dispatchEvent(new CustomEvent('agripos:token-refreshed', { detail: { token: newToken } }));
  } catch {
    // Refresh failed silently — the 401 interceptor will handle expiry
  } finally {
    _refreshing = false;
  }
}

// Global flag: components can set this to signal the 401 overlay
let _setSessionExpired = null;


export async function hashPinOffline(pin) {
  const encoder = new TextEncoder();
  const data = encoder.encode(pin + '_agripos_kiosk_v1');
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function handleOfflineRequest(config) {
  const path = config.url || '';
  const params = config.params || {};
  const method = (config.method || 'get').toLowerCase();

  if (method === 'get') {
    // Products list / search
    if (path === '/products' || path.startsWith('/products?')) {
      let products = await getProducts();
      const q = (params.search || '').toLowerCase();
      if (q) products = products.filter(p =>
        (p.name || '').toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q));
      if (params.is_repack !== undefined) {
        const wantRepack = params.is_repack === true || params.is_repack === 'true';
        products = products.filter(p => !!p.is_repack === wantRepack);
      }
      const skip = parseInt(params.skip) || 0;
      const limit = parseInt(params.limit) || 50;
      return { data: { products: products.slice(skip, skip + limit), total: products.length } };
    }

    // Product search-detail (POS search with branch prices + inventory)
    if (path === '/products/search-detail') {
      const q = (params.q || '').toLowerCase();
      const branchId = params.branch_id;
      let products = await getProducts();
      const filtered = products.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.sku || '').toLowerCase().includes(q) ||
        (p.barcode || '').includes(params.q || '')
      ).slice(0, 10);

      const enriched = await Promise.all(filtered.map(async p => {
        const inv = await getInventoryItem(p.id);
        const bp = branchId ? await getBranchPrice(p.id) : null;
        const prices = bp?.prices ? { ...(p.prices || {}), ...bp.prices } : (p.prices || {});
        const cost = bp?.cost_price ?? p.cost_price;
        return { ...p, prices, cost_price: cost, available: inv?.quantity ?? 0, reserved: 0, coming: 0 };
      }));
      return { data: enriched };
    }

    // Customers
    if (path === '/customers' || path.startsWith('/customers?')) {
      let customers = await getCustomers();
      const q = (params.search || '').toLowerCase();
      if (q) customers = customers.filter(c => (c.name || '').toLowerCase().includes(q));
      const skip = parseInt(params.skip) || 0;
      const limit = parseInt(params.limit) || 50;
      return { data: { customers: customers.slice(skip, skip + limit), total: customers.length } };
    }

    // Price schemes
    if (path === '/price-schemes') {
      const schemes = await getPriceSchemes();
      return { data: schemes };
    }

    // Inventory
    if (path === '/inventory' || path.startsWith('/inventory?')) {
      const inventory = await getInventory();
      return { data: { items: inventory, total: inventory.length } };
    }

    // Barcode lookup (for kiosk / POS barcode scanning)
    if (path.startsWith('/products/barcode-lookup/')) {
      const barcode = decodeURIComponent(path.split('/products/barcode-lookup/')[1]);
      const products = await getProducts();
      const found = products.find(p => p.barcode === barcode && p.active !== false);
      if (!found) {
        return Promise.reject({ response: { status: 404, data: { detail: 'No product found with this barcode' } } });
      }
      const inv = await getInventoryItem(found.id);
      const branchId = params.branch_id;
      const bp = branchId ? await getBranchPrice(found.id) : null;
      const prices = bp?.prices ? { ...(found.prices || {}), ...bp.prices } : (found.prices || {});
      const cost = bp?.cost_price ?? found.cost_price;
      return { data: { ...found, prices, cost_price: cost, available: inv?.quantity ?? 0 } };
    }
  }

  // Offline sale → queue to pending_sales
  if (method === 'post' && path === '/unified-sale') {
    const sale = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    sale.id = sale.id || `offline-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    sale.offline = true;
    sale.timestamp = new Date().toISOString();
    await addPendingSale(sale);
    return {
      data: {
        id: sale.id,
        invoice_number: `OFFLINE-${sale.id.toString().slice(-8).toUpperCase()}`,
        offline: true,
        status: 'queued',
        grand_total: sale.grand_total || 0,
        message: 'Sale saved offline — will sync when connected.',
      }
    };
  }

  // Offline PIN verification (for kiosk unlock / cost reveal / credit sale approval)
  if (method === 'post' && path === '/auth/verify-manager-pin') {
    const body = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    const pin = body.pin || '';
    if (!pin) {
      return { data: { valid: false, detail: 'PIN required' } };
    }
    // Primary path: bcrypt-hashed admin_pin from /sync/pos-data (offline robustness Iter 192)
    try {
      const { verifyOfflinePin } = await import('../lib/offlineAuth');
      const result = await verifyOfflinePin(pin);
      if (result.ok) {
        return {
          data: {
            valid: true,
            manager_id: 'offline_admin_pin',
            manager_name: 'Manager (offline)',
            role: 'admin_pin',
            method: result.method || 'admin_pin',
          },
        };
      }
    } catch { /* fall through to legacy kiosk cache */ }
    // Legacy fallback: SHA-256 kiosk_pin_cache (older flow)
    const cachedHash = localStorage.getItem('kiosk_pin_cache');
    if (cachedHash) {
      const inputHash = await hashPinOffline(pin);
      if (inputHash === cachedHash) {
        return { data: { valid: true, manager_id: 'offline_cached', manager_name: 'Manager (offline)', role: 'cached' } };
      }
    }
    return {
      data: {
        valid: false,
        detail: 'Invalid PIN. Offline mode accepts only the system Admin PIN. Sync once while online to refresh credentials.',
      },
    };
  }

  // Can't serve from cache — propagate original error
  return Promise.reject(new Error('Network unavailable'));
}

// Branch filter state - will be set by AuthProvider
let currentBranchFilter = null;
let isMultiBranchUser = false;

// Request interceptor: Add auth token and branch filter
api.interceptors.request.use(config => {
  const token = localStorage.getItem('agripos_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  
  // Auto-append branch_id for GET requests (data filtering)
  // Skip if already has branch_id, or if it's a "global" endpoint
  if (config.method === 'get' && currentBranchFilter && currentBranchFilter !== 'all') {
    const globalEndpoints = ['/branches', '/products', '/price-schemes', '/permissions', '/auth', '/branch-prices/propagation-history', '/customers/-/duplicates'];
    const isGlobal = globalEndpoints.some(ep => config.url?.startsWith(ep));
    
    if (!isGlobal && !config.params?.branch_id) {
      config.params = { ...config.params, branch_id: currentBranchFilter };
    }
  }
  
  return config;
});

api.interceptors.response.use(
  res => {
    // On every successful response, opportunistically check if the token
    // needs a silent refresh (< 6 h remaining). Fire-and-forget.
    maybeSilentRefresh();
    return res;
  },
  async err => {
    // Only serve from IndexedDB when the device is genuinely offline.
    // Do NOT intercept when online — backend errors should propagate normally.
    if (!navigator.onLine && !err.response && err.config) {
      try {
        return await handleOfflineRequest(err.config);
      } catch {
        // Cache miss — fall through
      }
    }
    if (err.response?.status === 401) {
      // Don't hard-redirect. Show the re-login overlay so the user can
      // authenticate inline and resume without losing unsaved page state.
      if (typeof _setSessionExpired === 'function') {
        _setSessionExpired(true);
      } else {
        // AuthProvider not mounted yet (e.g. during initial load) — fall back
        localStorage.removeItem('agripos_token');
        localStorage.removeItem('agripos_user');
        window.location.href = '/login';
      }
    }
    // Subscription expired (soft-lock) → show toast + redirect
    if (err.response?.status === 402) {
      const detail = err.response.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message : detail;
      if (msg && !window._softLockToastShown) {
        window._softLockToastShown = true;
        setTimeout(() => { window._softLockToastShown = false; }, 5000);
        // Use a global toast if available, otherwise alert
        if (typeof window.showSoftLockToast === 'function') {
          window.showSoftLockToast(msg);
        }
      }
    }
    return Promise.reject(err);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('agripos_token'));
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Wire up the module-level setter so the response interceptor can trigger
  // the overlay without needing a React ref passed through module scope.
  useEffect(() => {
    _setSessionExpired = setSessionExpired;
    return () => { _setSessionExpired = null; };
  }, []);

  // When a background silent refresh succeeds, sync the token state here.
  useEffect(() => {
    const handler = (e) => {
      const newToken = e.detail?.token;
      if (newToken) {
        setToken(newToken);
      }
    };
    window.addEventListener('agripos:token-refreshed', handler);
    return () => window.removeEventListener('agripos:token-refreshed', handler);
  }, []);
  
  // Branch selection: 'all' for consolidated view, or specific branch ID
  const [selectedBranchId, setSelectedBranchId] = useState(() => {
    return localStorage.getItem('agripos_selected_branch') || 'all';
  });

  // The user's authoritative branch whitelist. A user can ONLY switch into
  // / receive data for branches in this list (admins are unscoped).
  // Sources, in order:
  //   1. `branch_ids` array (multi-branch assignment, new model)
  //   2. legacy single `branch_id` (folded into a 1-element list)
  //   3. empty list = "All Branches" legacy unscoped (same as admin)
  const assignedBranchIds = useMemo(() => {
    if (!user) return [];
    const list = Array.isArray(user.branch_ids) ? [...user.branch_ids] : [];
    if (user.branch_id && !list.includes(user.branch_id)) list.push(user.branch_id);
    return list.filter(Boolean);
  }, [user]);

  // Determine if user can pick from a branch dropdown (vs. being locked to
  // a single branch). Admins are always unscoped. Multi-branch users (2+)
  // get the dropdown. Legacy "no assignment" users also get it (treated
  // as unscoped, matching their pre-multi-branch behaviour).
  const canViewAllBranches = useMemo(() => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (assignedBranchIds.length === 0) return true;   // legacy unscoped
    if (assignedBranchIds.length >= 2) return true;    // multi-branch manager/auditor
    return false;
  }, [user, assignedBranchIds]);

  // Get the current branch object (or null if viewing all)
  const currentBranch = useMemo(() => {
    if (selectedBranchId === 'all') return null;
    return branches.find(b => b.id === selectedBranchId) || null;
  }, [selectedBranchId, branches]);

  // Effective branch ID for API calls.
  //   - Admins / unscoped users → whatever they've selected ('all' or specific)
  //   - Single-branch users    → forced to their one branch
  //   - Multi-branch users     → can switch among their assigned branches;
  //     'all' is allowed too IF they have 2+ branches (consolidated view
  //     is gated to their whitelist via the API interceptor below + the
  //     backend guard `assert_branch_access`)
  const effectiveBranchId = useMemo(() => {
    if (!user) return selectedBranchId;
    // Admin → unscoped
    if (user.role === 'admin') return selectedBranchId;
    // Single-branch user with no branch_ids list → locked to legacy branch_id
    if (assignedBranchIds.length === 1) return assignedBranchIds[0];
    // No assignment at all (legacy unscoped) → behave like admin within org
    if (assignedBranchIds.length === 0) return selectedBranchId;
    // Multi-branch (2+): allow the chosen one, but if it's not in the
    // whitelist (stale localStorage from another account), snap back to
    // 'all' to avoid silent 403s on every page load.
    if (selectedBranchId === 'all') return 'all';
    if (assignedBranchIds.includes(selectedBranchId)) return selectedBranchId;
    return 'all';
  }, [user, selectedBranchId, assignedBranchIds]);

  // Update the interceptor's branch filter
  useEffect(() => {
    currentBranchFilter = effectiveBranchId;
    isMultiBranchUser = canViewAllBranches;
  }, [effectiveBranchId, canViewAllBranches]);

  const fetchUser = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    // ── Offline reload guard ──
    // When the browser is offline (or backend is unreachable), /auth/me will
    // fail and would log the cashier out — losing pending sales context if
    // they accidentally pressed reload. Hydrate from the cached user
    // snapshot so the session survives the reload, and re-validate when
    // the network returns.
    const cachedUserRaw = localStorage.getItem('agripos_user');
    let cachedUser = null;
    try { cachedUser = cachedUserRaw ? JSON.parse(cachedUserRaw) : null; } catch { cachedUser = null; }

    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      localStorage.setItem('agripos_user', JSON.stringify(res.data));
      // Scope offline DB to this organization
      if (res.data.organization_id) setOfflineOrg(res.data.organization_id);
      // Store cashier receipt prefix for offline numbering
      if (res.data.cashier_prefix) setCashierPrefix(res.data.cashier_prefix);

      // Cache the org's configured timezone so synchronous helpers like
      // `localToday()` can resolve dates in the tenant's wall-clock time
      // (critical for multi-tenant: a PH tenant sees 2026-05-01 while a US
      // tenant still on 2026-04-30 will not drift into each other).
      try {
        const tzRes = await api.get('/settings/timezone');
        if (tzRes.data?.timezone) {
          localStorage.setItem('agribooks.org_tz', tzRes.data.timezone);
        }
      } catch { /* best-effort; falls back to browser-local */ }

      // Cache the company logo as base64 on every login so:
      //   - Offline printing always has the logo available
      //   - PrintEngine reads it from localStorage without network
      //   - R2 presigned URLs are refreshed automatically (7-day max)
      // Fire-and-forget — logo cache miss is non-blocking.
      try {
        const bizRes = await api.get('/settings/business-info');
        const bizData = bizRes.data || {};
        if (bizData.logo_url) {
          const b64 = await urlToBase64(bizData.logo_url);
          if (b64) {
            bizData.logo_base64 = b64;
            // Store in localStorage for PrintEngine fast-path
            const orgId = res.data.organization_id || '';
            localStorage.setItem(`agribooks_logo_${orgId}`, b64);
          }
          await saveBusinessInfoCache(bizData);
        }
      } catch { /* best-effort — logo is non-critical for auth */ }

      let branchList = [];
      try {
        const branchRes = await api.get('/branches');
        branchList = branchRes.data || [];
        setBranches(branchList);
        localStorage.setItem('agripos_branches', JSON.stringify(branchList));
      } catch {
        // Use cached branches if branch fetch failed
        try {
          const cachedBranches = JSON.parse(localStorage.getItem('agripos_branches') || '[]');
          branchList = cachedBranches;
          setBranches(cachedBranches);
        } catch { /* ignore */ }
      }

      // If user is branch-restricted, force that branch
      if (res.data.branch_id) {
        setSelectedBranchId(res.data.branch_id);
        localStorage.setItem('agripos_selected_branch', res.data.branch_id);
      } else {
        // Admin/owner: use saved preference or default to 'all'
        const saved = localStorage.getItem('agripos_selected_branch');
        if (saved && (saved === 'all' || branchList.some(b => b.id === saved))) {
          setSelectedBranchId(saved);
        } else {
          setSelectedBranchId('all');
        }
      }
    } catch {
      // /auth/me failed — could be offline OR a real auth failure (401/403).
      // We can't distinguish without the response code in this catch, so we
      // do a lightweight ping to /api/health: if reachable AND /auth/me
      // failed, the token is genuinely invalid → log out. If unreachable,
      // hydrate from cache and stay logged in.
      let backendReachable = false;
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 4000);
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`, {
          method: 'GET', signal: ctrl.signal, cache: 'no-store',
        });
        clearTimeout(t);
        backendReachable = r.ok;
      } catch { backendReachable = false; }

      if (!backendReachable && cachedUser) {
        // Offline reload — keep the user signed in with cached snapshot.
        setUser(cachedUser);
        if (cachedUser.organization_id) setOfflineOrg(cachedUser.organization_id);
        if (cachedUser.cashier_prefix) setCashierPrefix(cachedUser.cashier_prefix);
        try {
          const cachedBranches = JSON.parse(localStorage.getItem('agripos_branches') || '[]');
          setBranches(cachedBranches);
          if (cachedUser.branch_id) {
            setSelectedBranchId(cachedUser.branch_id);
          } else {
            const saved = localStorage.getItem('agripos_selected_branch');
            setSelectedBranchId(saved || 'all');
          }
        } catch { /* ignore */ }
      } else {
        // Backend reachable but /auth/me rejected → real auth failure.
        localStorage.removeItem('agripos_token');
        localStorage.removeItem('agripos_user');
        setToken(null);
      }
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (identifier, password) => {
    // Support both email and username via the 'email' field (backend checks both)
    const res = await api.post('/auth/login', { email: identifier, password });
    localStorage.setItem('agripos_token', res.data.token);
    // Merge subscription into user so components like OnboardingWizard
    // can access is_demo, effective_plan, etc. immediately at login.
    const userData = res.data.user;
    if (res.data.subscription) {
      userData.subscription = res.data.subscription;
    }
    localStorage.setItem('agripos_user', JSON.stringify(userData));
    setToken(res.data.token);
    setUser(userData);
    setSessionExpired(false);  // clear overlay if it was showing
    // Scope offline DB to this organization
    if (res.data.user?.organization_id) setOfflineOrg(res.data.user.organization_id);
    // Store cashier receipt prefix for offline numbering
    if (res.data.user?.cashier_prefix) setCashierPrefix(res.data.user.cashier_prefix);
    
    // Store subscription info if present
    if (res.data.subscription) {
      localStorage.setItem('agripos_subscription', JSON.stringify(res.data.subscription));
    }
    
    // Set branch based on user's access
    if (res.data.user.branch_id) {
      setSelectedBranchId(res.data.user.branch_id);
      localStorage.setItem('agripos_selected_branch', res.data.user.branch_id);
    } else {
      setSelectedBranchId('all');
      localStorage.setItem('agripos_selected_branch', 'all');
    }
    
    return res.data;
  };

  const logout = async () => {
    // Check for pending offline sales before logout
    try {
      const { hasPendingSales } = await import('../lib/offlineDB');
      const pending = await hasPendingSales();
      if (pending) {
        const confirmed = window.confirm(
          'You have unsynced offline sales! If you log out now, they will sync next time you log in.\n\nAre you sure you want to log out?'
        );
        if (!confirmed) return;
      }
    } catch { /* proceed with logout */ }

    localStorage.removeItem('agripos_token');
    localStorage.removeItem('agripos_user');
    localStorage.removeItem('agripos_selected_branch');
    localStorage.removeItem('agripos_subscription');
    setToken(null);
    setUser(null);
    setBranches([]);
    setSelectedBranchId('all');
  };

  const switchBranch = (branchId) => {
    if (!user) return;
    // Admin → unrestricted, can pick anything (incl. 'all')
    // Multi-branch user → may pick 'all' OR any branch in their assignment list
    // Legacy unscoped user (no assignment) → may pick anything within their org
    // Single-branch user → may only pick their one branch (no-op switch)
    if (user.role === 'admin') {
      // ok
    } else if (assignedBranchIds.length === 0) {
      // legacy unscoped — any branch in org allowed
    } else {
      const allowed = branchId === 'all' || assignedBranchIds.includes(branchId);
      if (!allowed) return;  // silently refuse off-list switches
    }
    setSelectedBranchId(branchId);
    localStorage.setItem('agripos_selected_branch', branchId);
  };

  const hasPerm = (module, action) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (user.permissions?.[module]?.[action]) return true;
    // Check delegated access
    if (delegations[module]) return true;
    return false;
  };

  // Delegation system — temporary section access via TOTP/PIN
  const [delegations, setDelegations] = useState({});

  const requestSectionOverride = async (module, pin) => {
    const res = await api.post('/auth/section-override', {
      module,
      pin,
      current_token: token,
    });
    if (res.data.valid) {
      // Update token with delegation embedded
      const newToken = res.data.token;
      localStorage.setItem('agripos_token', newToken);
      setToken(newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setDelegations(res.data.delegations || {});
      return res.data;
    }
    throw new Error(res.data.error || 'Invalid PIN/TOTP');
  };

  // On login, extract delegations from token if present
  const extractDelegations = useCallback((tkn) => {
    if (!tkn) return;
    try {
      const payload = JSON.parse(atob(tkn.split('.')[1]));
      if (payload.delegations) setDelegations(payload.delegations);
    } catch {}
  }, []);

  useEffect(() => { extractDelegations(token); }, [token, extractDelegations]);

  // View mode helpers
  const isConsolidatedView = selectedBranchId === 'all' && canViewAllBranches;
  const viewingBranchName = currentBranch?.name || (isConsolidatedView ? 'All Branches' : 'Unknown');

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout,
      sessionExpired,
      // Branch management
      branches,
      currentBranch,           // Current branch object (null if viewing all)
      selectedBranchId,        // 'all' or specific branch ID
      effectiveBranchId,       // What to use for API calls
      canViewAllBranches,      // Whether user can switch branches
      isConsolidatedView,      // true if viewing all branches
      viewingBranchName,       // Display name for current view
      switchBranch,
      assignedBranchIds,       // Branches the user is allowed to operate in
                               // (empty = legacy unscoped / admin-style access)
      // Permissions
      hasPerm,
      // Delegation
      delegations,
      requestSectionOverride,
      refreshUser: fetchUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
